import { useState } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { useDialog, useUserConfig } from "app/hook";
import I18n from "common/i18n";
import { ClickEvent } from "common/types";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { Switch, CloseBtn, FadeTransition } from "common/elements";
import { ContactType } from "features/universal/Contact";
import { captureScreen } from "features/universal/captureScreen";

/**
 * 端末を振ったときに出る「バグを報告しますか？」の確認ダイアログ。
 *
 * 勝手に撮られると怖いので、振っただけでは画面を撮らない。ボタンは 2 つで、
 * 「おといあわせ」は何も添付せずに、「がめんを つけて」は押したときに撮った画面を添付して、お問い合わせ画面を開く。
 * ここで振って報告する機能自体をオフにもできる（ユーザー設定と同じ値）。
 */
export const ShakeBugReportDialog = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const handleChangeUserConfig = useUserConfig();
  const shakeBugReport = useSelector(
    (state: RootState) => state.config.userConfig.shake_bug_report,
  );
  const shake = useSelector((state: RootState) => state.config.dialog.shake_bug_report);
  const [capturing, setCapturing] = useState(false);

  const handleClose = () => {
    handleDialog({ type: DialogType.SHAKE_BUG_REPORT, value: false, args: {} });
  };

  const handleCancel = () => {
    play();
    btnDelay(handleClose);
  };

  const openContact = (capture: string | null) => {
    handleClose();
    handleDialog({
      type: DialogType.CONTACT,
      value: true,
      args: { type: ContactType.BUG, ...(capture ? { capture } : {}) },
    });
  };

  const handleReport = () => {
    play();
    btnDelay(() => openContact(null));
  };

  // この層は capture-ignore なので、開いたまま撮っても振ったときの画面が写る
  const handleReportWithCapture = () => {
    if (capturing) return;
    play();
    setCapturing(true);
    btnDelay(async () => {
      const capture = await captureScreen();
      setCapturing(false);
      openContact(capture);
    });
  };

  const handleToggleShake = (e: ClickEvent) => {
    e.stopPropagation();
    handleChangeUserConfig("shake_bug_report", !shakeBugReport, true);
  };

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      open={shake.value}
      className="capture-ignore"
      sx={{ zIndex: 100001 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
      onClose={handleClose}
    >
      {/* transform: scale() はレイアウトサイズを変えないので、Paper に直接かけると
          はみ出す。fullScreen の上に敷いた全面レイヤーの flex-center で中央に寄せる
          （responsive-scaling.md / Contact・Feedback と同じ方式） */}
      <div className="flex-col-center !absolute inset-0 bg-textcolor-black/50 w-full h-full">
        <div
          className="flex-col-el justify-start w-[720px] relative border-solid border-white border-8 rounded-lg bg-white"
          style={{ transform: `scale(${scale})` }}
        >
          <CloseBtn close={handleCancel} />

          <div className="flex-col-view justify-start w-full relative box-border p-6 rounded bg-beige-60">
            <div className="flex-row-view items-center justify-center gap-2 pb-4">
              <span className="material-symbols-outlined type-title text-orange-100 shrink-0">
                bug_report
              </span>
              <p className="type-text type-title text-textcolor-black leading-tight">
                {I18n.t("MSG_SHAKE_BUG_REPORT_TITLE")}
              </p>
            </div>

            <p className="type-text type-body text-textcolor-black leading-tight whitespace-pre-line pb-4">
              {I18n.t("MSG_SHAKE_BUG_REPORT_DESCRIPTION")}
            </p>

            {/* 画面を撮るのは、右のボタンを押したときだけ */}
            <div className="flex-row-el flex-center gap-4 alpha">
              <button
                type="button"
                disabled={capturing}
                onClick={handleReport}
                className="type-inherit-font btn btn-blue whitespace-nowrap"
              >
                <div className="flex-row-center gap-2">
                  <span className="material-symbols-outlined type-title text-white">mail</span>
                  <span className="type-text type-body text-white">
                    {I18n.t("MSG_SHAKE_BUG_REPORT_CONTACT_BTN")}
                  </span>
                </div>
              </button>
              <button
                type="button"
                disabled={capturing}
                onClick={handleReportWithCapture}
                className="type-inherit-font btn btn-primary whitespace-nowrap"
              >
                <div className="flex-row-center gap-2">
                  <span className="material-symbols-outlined type-title text-white">
                    screenshot_monitor
                  </span>
                  <span className="type-text type-body text-white">
                    {I18n.t("MSG_SHAKE_BUG_REPORT_CAPTURE_BTN")}
                  </span>
                </div>
              </button>
            </div>

            {/* 見出しとトグルを同じ行に置く。説明を同じ行に入れるとトグルが
                上下中央に寄って 3 行がばらけて見えるため、説明は下の全幅に出す */}
            <div className="flex-col-el gap-1 mt-6 pt-4 border-t-2 border-b-0 border-x-0 border-solid border-gray-20">
              <div className="flex-row-el items-center justify-between gap-4 w-full">
                <p className="type-text !text-left type-body text-textcolor-black leading-tight">
                  {I18n.t("MSG_SHAKE_BUG_REPORT_TOGGLE_LABEL")}
                </p>
                <Switch
                  color="#B8B1B5"
                  background="#DBEBFC"
                  thumb="#94C4F7"
                  margin={2}
                  width={80}
                  height={36}
                  value={shakeBugReport}
                  onClick={handleToggleShake}
                />
              </div>
              <p className="type-text !text-left type-sm text-gray2-100 leading-tight">
                {I18n.t("MSG_SHAKE_BUG_REPORT_TOGGLE_DESCRIPTION")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
